import { ONBOARDING_USER_TYPE, parseOnboardingLinkSearch, resolveOnboardingUserId } from './onboardingLinkParams';
import { usesSubscriptionActivationVerify } from './onboardingApi';

/** Invitation links with userId (+ userType) run validate-invitation before the form. */
export function needsInvitationValidation(linkParams) {
  return Boolean(linkParams.userId && linkParams.userType);
}

/**
 * Invitation links already run validate-invitation, so account-step validate-email can be skipped.
 */
export function shouldSkipAccountEmailVerification(linkParams, invitationValidationState) {
  if (invitationValidationState === 'valid') return true;
  if (linkParams.userId) return true;
  return false;
}

/**
 * Maps GET onboarding/validate-invitation response into registration patches.
 */
export function mapOrgUserInvitationResponse(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const data = payload.data && typeof payload.data === 'object' && !payload.email ? payload.data : payload;
  const pick = (...keys) => {
    for (let i = 0; i < keys.length; i += 1) {
      const value = data[keys[i]];
      if (value != null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
  };

  const email = pick('email');
  const firstName = pick('firstName');
  const lastName = pick('lastName');
  const businessName = pick('fullBusinessName', 'fullBusinessname');
  const tradingName = pick('tradingName');
  const registrationNumber = pick('businessRegistrationNumber');
  const planName = pick('planName');
  const billingCurrency = pick('billingCurrency').toUpperCase();
  const userId = pick('userId', 'id') || null;

  if (
    !email &&
    !firstName &&
    !lastName &&
    !businessName &&
    !tradingName &&
    !registrationNumber &&
    !planName &&
    !billingCurrency &&
    !userId
  ) {
    return null;
  }

  return {
    account: {
      ...(email ? { email } : {}),
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
    },
    organisation: {
      ...(businessName ? { businessName } : {}),
      ...(tradingName ? { tradingName } : {}),
      ...(registrationNumber ? { registrationNumber } : {}),
    },
    plan: {
      ...(planName ? { name: planName } : {}),
      ...(billingCurrency ? { currency: billingCurrency } : {}),
    },
    userId,
    invitationId: userId,
    emailFromApi: Boolean(email),
  };
}

export function applyOrgUserInvitationPrefill(registration, invitationPatch) {
  if (!registration || !invitationPatch) return registration;

  const userId = invitationPatch.userId || invitationPatch.invitationId;

  return {
    ...registration,
    account: {
      ...registration.account,
      ...invitationPatch.account,
    },
    organisation: {
      ...registration.organisation,
      ...invitationPatch.organisation,
    },
    plan: {
      ...registration.plan,
      ...invitationPatch.plan,
    },
    invitation: {
      ...registration.invitation,
      ...(userId
        ? {
            userId,
            invitationId: userId,
          }
        : {}),
    },
  };
}

export function getInitialInvitationValidationState(linkParams) {
  return needsInvitationValidation(linkParams) ? 'pending' : 'not-required';
}

export function getInitialRegistration(linkParams, defaultPlanCurrency) {
  const billingCurrency =
    (defaultPlanCurrency && String(defaultPlanCurrency).trim().toUpperCase()) || 'AUD';
  const userId = resolveOnboardingUserId(linkParams);

  return {
    account: {
      email: linkParams.email || '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    organisation: {
      businessName: '',
      tradingName: '',
      registrationNumber: '',
      industry: undefined,
      regulatoryBody: undefined,
    },
    plan: {
      name: '',
      currency: billingCurrency,
    },
    activation: {
      subscriptionKey: linkParams.subscriptionKey || '',
      promoCode: linkParams.promoCode || '',
    },
    brokerCreatingPlan: null,
    userType: linkParams.userType,
    invitation: {
      userId,
      invitationId: userId,
      brokerageId: linkParams.brokerageId || null,
    },
  };
}

export function createOnboardingRegistrationState(locationSearch, defaultPlanCurrency) {
  const linkParams = parseOnboardingLinkSearch(locationSearch);
  const initialRegistration = getInitialRegistration(linkParams, defaultPlanCurrency);
  const needsActivationVerify = usesSubscriptionActivationVerify(linkParams);
  const invitationValidationState = getInitialInvitationValidationState(linkParams);
  const awaitingInvitationValidation = invitationValidationState === 'pending';
  const invitationReady =
    Boolean(linkParams.userId && linkParams.userType) && !awaitingInvitationValidation;
  const autoStartReady =
    linkParams.canAutoStart && !needsActivationVerify && !awaitingInvitationValidation;

  return {
    activeStep: 0,
    registration: initialRegistration,
    completedSteps: [],
    isSubmitting: false,
    isActivated: false,
    preRegistrationComplete: autoStartReady || invitationReady,
    linkParams,
    invitationValidationState,
    validationError: null,
    isValidating: false,
    activationError: null,
    isVerifyingActivation: false,
    isVerifyingEmail: false,
    emailVerificationError: null,
    pendingOtpVerification: false,
    otpUserId: null,
    otpError: null,
    isVerifyingOtp: false,
    isResendingOtp: false,
    otpResendAvailableAt: null,
  };
}

export function canShowRegistrationForm(state) {
  const {
    preRegistrationComplete,
    invitationValidationState,
    validationError,
    activationError,
    isActivated,
  } = state;

  if (isActivated) return false;
  if (!preRegistrationComplete) return false;
  if (invitationValidationState === 'pending' || invitationValidationState === 'invalid') return false;
  if (validationError || activationError) return false;
  return true;
}

export function isBrokerOnboardingPath(linkParams, userType) {
  if (hasSubscriptionOrPromoActivation({}, linkParams) && !resolveOnboardingUserId(linkParams)) {
    return false;
  }
  if (userType === ONBOARDING_USER_TYPE.ORG_USER) return false;
  if (userType === ONBOARDING_USER_TYPE.NORMAL_USER) return false;
  if (userType === ONBOARDING_USER_TYPE.BROKER) return true;
  return linkParams.hasActivationFromLink;
}

function hasPromoCodeActivation(linkParams, registration = {}) {
  const promoCode = String(registration.activation?.promoCode || linkParams.promoCode || '').trim();
  return Boolean(promoCode);
}

export function isStrictBrokerOnboarding(linkParams, registration = {}) {
  if (hasSubscriptionOrPromoActivation(registration, linkParams) && !resolveOnboardingUserId(linkParams, registration)) {
    return false;
  }
  const userType = registration.userType ?? linkParams.userType;
  return userType === ONBOARDING_USER_TYPE.BROKER || linkParams.isBrokerPath;
}

/** Promo-code onboarding never shows skip. Brokers who chose to create a plan may skip optional steps. */
export function shouldShowSkipOrganisation(linkParams, registration = {}) {
  if (hasPromoCodeActivation(linkParams, registration)) return false;
  if (!isStrictBrokerOnboarding(linkParams, registration)) return false;
  return registration.brokerCreatingPlan === true;
}

export function shouldShowSkipPlan(linkParams, registration = {}) {
  if (hasPromoCodeActivation(linkParams, registration)) return false;
  if (!isStrictBrokerOnboarding(linkParams, registration)) return false;
  return registration.brokerCreatingPlan === true;
}

function trimActivationValue(value) {
  return value == null ? '' : String(value).trim();
}

export function mergeRegistrationActivation(registration = {}, linkParams = {}, activationValues = {}) {
  return {
    subscriptionKey: trimActivationValue(
      activationValues.subscriptionKey ??
        registration.activation?.subscriptionKey ??
        linkParams.subscriptionKey,
    ),
    promoCode: trimActivationValue(
      activationValues.promoCode ?? registration.activation?.promoCode ?? linkParams.promoCode,
    ),
  };
}

export function hasSubscriptionOrPromoActivation(registration = {}, linkParams = {}) {
  const { subscriptionKey, promoCode } = mergeRegistrationActivation(registration, linkParams);
  return Boolean(subscriptionKey || promoCode);
}

export function isBrokerRegistration(registration = {}, linkParams = {}) {
  // Promo / subscription-key onboarding is never the broker invitation path.
  if (
    hasSubscriptionOrPromoActivation(registration, linkParams) &&
    !resolveOnboardingUserId(linkParams, registration)
  ) {
    return false;
  }
  const userType = registration.userType ?? linkParams.userType;
  return userType === ONBOARDING_USER_TYPE.BROKER || linkParams.isBrokerPath;
}

/** Org / normal user registration completes with email OTP; brokers receive access_token directly. */
export function requiresOtpVerificationAfterSubmit(registration = {}, linkParams = {}) {
  return !isBrokerRegistration(registration, linkParams);
}

export const ONBOARDING_OTP_RESEND_COOLDOWN_MS = 60_000;

export function maskEmailAddress(email) {
  const value = trimActivationValue(email);
  if (!value || !value.includes('@')) return value;

  const [localPart, domain] = value.split('@');
  if (!localPart || !domain) return value;
  if (localPart.length <= 1) return `*@${domain}`;

  const hiddenLength = Math.min(Math.max(localPart.length - 1, 1), 4);
  return `${localPart[0]}${'*'.repeat(hiddenLength)}@${domain}`;
}

export function getOtpVerificationDescription(email) {
  const masked = maskEmailAddress(email);
  if (masked) {
    return `Enter the verification code we sent to ${masked}. The code expires after a short time.`;
  }
  return 'Enter the verification code we sent to your email address.';
}

export function getOnboardingTagline(linkParams) {
  if (hasSubscriptionOrPromoActivation({}, linkParams) && !resolveOnboardingUserId(linkParams)) {
    return "Let's get you set up in just a few steps - complete each section below.";
  }
  if (linkParams.isBrokerPath) {
    return 'Set up your broker account in just a few steps - complete each section below.';
  }
  if (linkParams.isOrgUserPath || linkParams.isNormalUserPath) {
    return 'Set up your organisation account in just a few steps - complete each section below.';
  }
  return "Let's get you set up in just a few steps - complete each section below.";
}

export function getRegistrationSuccessDescription(linkParams) {
  if (hasSubscriptionOrPromoActivation({}, linkParams) && !resolveOnboardingUserId(linkParams)) {
    return 'Your account has been created. Redirecting to your dashboard.';
  }
  if (linkParams.isOrgUserPath || linkParams.isNormalUserPath) {
    return 'Your account has been created. Redirecting to your dashboard.';
  }
  return 'Your broker account has been created. Redirecting to your dashboard.';
}
